import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { generateId } from '../../utils/DomUtil';
import { PassiveAggressiveTimer } from '../../utils/PassiveAggressiveTimer';
import { ToastAnimator } from './ToastAnimator';
import { ToastBaseProps, ToastBaseWidget, ToastType } from './ToastBase';
import { ToastProps } from './ToastWidget';

export type ToastId = string;

export interface ToasterProps {
  style?: React.CSSProperties;
}

export interface ToasterState {
  toasts: Array<{ id: ToastId, props: ToastProps }>;
}

export const FadeTransitionTimeMS: number = 300;
export const AutodimissCloseFadeTransitionTimeMS: number = 1000;
export const HoverRestoreFadeTransitionTimeMS: number = 30;
export const DefaultAutoDismissTimeMS: number = 5000;

export const ToastMaxWidth = 800;

export const ToasterStyles: {
  centeringStyle: React.CSSProperties,
  positioningDiv: React.CSSProperties,
  toastDiv: React.CSSProperties,
} = Object.freeze({
  centeringStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: ToastMaxWidth
  },
  positioningDiv: {
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    position: 'fixed',
    right: 0,
    top: 0,
    visibility: 'hidden', // TFS 843927 don't interfere with pointer events of headers underneath
  },
  toastDiv: {
    transitionProperty: 'transform, opacity',
  },
});

const DefaultToastProps: Pick<ToastProps, 'autoDismiss' | 'autoDismissTimeMS'> = Object.freeze({
  autoDismiss: true,
  autoDismissTimeMS: DefaultAutoDismissTimeMS,
});

function shouldAutoDismiss(props: ToastProps): boolean {
  if (props.toastType === ToastType.Error || props.toastType === ToastType.Warning ||
    props.actionMessage || props.onAction) {
    return false;
  }
  return !!props.autoDismiss;
}

function extractBaseToastProps(props: ToastProps): ToastBaseProps {
  // tslint:disable-next-line:deprecation
  const { autoDismiss, autoDismissTimeMS, topLeftXPx, topLeftYPx, zIndex, ...baseToastProps } = props;
  return baseToastProps;
}

export class Toaster extends React.PureComponent<ToasterProps, ToasterState> {
  public state: ToasterState = { toasts: [] };

  public componentDidUpdate(prevProps: ToasterProps, prevState: ToasterState): void {
    const currToasts = this.state.toasts;
    prevState.toasts.forEach(oldToast => {
      // can't do a simple `filter` since we need to match on toast id rather than (cloned) toast object
      if (!currToasts.find(toast => toast.id === oldToast.id)) {
        oldToast.props.onCloseRequest?.();
      }
    });
  }

  public show(props: ToastProps): ToastId {
    const id = generateId();
    this.setState(({ toasts }) => ({
      toasts: toasts.concat({
        id,
        props: { ...DefaultToastProps, ...props }
      })
    }));
    return id;
  }

  public update(toastId: ToastId, newProps: Partial<ToastProps>): void {
    this.setState(({ toasts }) => {
      const toastIndex = toasts.findIndex(({ id }) => id === toastId);
      if (toastIndex < 0) {
        console.warn(`found no toast matching id ${toastId}`);
        return null;
      }

      const toastsCopy = [...toasts];
      const mergedProps = { ...toastsCopy[toastIndex].props, ...newProps };
      toastsCopy.splice(toastIndex, 1, { id: toastId, props: mergedProps });
      return { toasts: toastsCopy };
    });
  }

  public dismiss(toastId: ToastId): void {
    this.setState(({ toasts }) => {
      const toastToRemove = toasts.find(({ id }) => id === toastId);
      if (!toastToRemove) {
        console.warn(`found no toast matching id ${toastId}`);
        return;
      }
      return { toasts: toasts.filter(t => t !== toastToRemove) };
    });
  }

  private getMaxZIndex(): number | undefined {
    let zIndexes: number[] = this.state.toasts.map(({ props }) => props.zIndex!);
    zIndexes.push(this.props.style?.zIndex as number);
    zIndexes = zIndexes.filter(z => z !== undefined);
    return zIndexes.length ? Math.max(...zIndexes) : undefined;
  }

  public render(): React.ReactNode {
    const toasterStyle: React.CSSProperties = { ...this.props.style, zIndex: this.getMaxZIndex() };

    return <div className={TypeStyle.style(ToasterStyles.positioningDiv)} style={toasterStyle}>
      <ToastAnimator style={ToasterStyles.centeringStyle}>
        {this.state.toasts.map(({ id, props }) => {
          const toastWidget = <ToastBaseWidget
            {...extractBaseToastProps(props)}
            id={id}
            key={id}
            onCloseRequest={() => this.dismiss(id)}
          />;

          return !shouldAutoDismiss(props) ? toastWidget : <PassiveAggressiveTimer
            children={toastWidget}
            timerDuration={props.autoDismissTimeMS!}
            handleTimerElapsed={() => this.dismiss(id)}
            key={id}
          />;
        }).reverse()}
      </ToastAnimator>
    </div>;
  }
}
